import { useCallback, useEffect, useRef, useState } from 'react';
import { RotateCw } from 'lucide-react';

/**
 * Drag-to-rotate 360° product spin — BUILD-PLAN §62, §86.
 *
 * A genuine 360 spin is a photographed frame sequence: the product on a
 * turntable, typically 24–36 exposures. This component plays that sequence
 * and maps horizontal drag to frame index.
 *
 * It cannot invent those frames. §86 forbids faking a feature, so when a
 * product has no frame set the caller renders the normal gallery instead —
 * see ProductGallery. Nothing here pretends to spin a product that was never
 * photographed for it.
 *
 * Interaction notes:
 *   • Pointer Events cover mouse, touch and pen in one code path.
 *   • Pointer capture keeps the drag alive when the cursor leaves the frame,
 *     so the spin does not stick halfway.
 *   • Arrow keys step frames, so the control is not drag-only (§40).
 */

interface Product360ViewerProps {
  /** Ordered frame URLs, one per step around the product. */
  frames: string[];
  productName: string;
  /** Pixels of horizontal travel per frame step. */
  sensitivity?: number;
}

export function Product360Viewer({
  frames,
  productName,
  sensitivity = 12,
}: Product360ViewerProps) {
  const [index, setIndex] = useState(0);
  const [dragging, setDragging] = useState(false);
  const [ready, setReady] = useState(false);

  const startX = useRef(0);
  const startIndex = useRef(0);

  const total = frames.length;

  /** Wraps in both directions so the spin is continuous. */
  const step = useCallback(
    (delta: number) => {
      setIndex((current) => ((current + delta) % total + total) % total);
    },
    [total],
  );

  /*
   * Preload every frame before enabling interaction. A spin that fetches
   * frames mid-drag stutters badly, which reads as a broken feature rather
   * than a slow one.
   */
  useEffect(() => {
    let cancelled = false;
    let loaded = 0;

    frames.forEach((src) => {
      const image = new Image();
      image.onload = image.onerror = () => {
        loaded += 1;
        if (!cancelled && loaded === frames.length) {
          setReady(true);
        }
      };
      image.src = src;
    });

    return () => {
      cancelled = true;
    };
  }, [frames]);

  function handlePointerDown(event: React.PointerEvent<HTMLDivElement>) {
    event.currentTarget.setPointerCapture(event.pointerId);
    setDragging(true);
    startX.current = event.clientX;
    startIndex.current = index;
  }

  function handlePointerMove(event: React.PointerEvent<HTMLDivElement>) {
    if (!dragging) return;

    const travelled = event.clientX - startX.current;
    const steps = Math.round(travelled / sensitivity);

    setIndex(((startIndex.current - steps) % total + total) % total);
  }

  function handlePointerUp(event: React.PointerEvent<HTMLDivElement>) {
    event.currentTarget.releasePointerCapture(event.pointerId);
    setDragging(false);
  }

  return (
    <div
      role="group"
      aria-label={`${productName} — 360 degree view`}
      tabIndex={0}
      onKeyDown={(event) => {
        if (event.key === 'ArrowLeft') {
          event.preventDefault();
          step(-1);
        }
        if (event.key === 'ArrowRight') {
          event.preventDefault();
          step(1);
        }
      }}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerCancel={handlePointerUp}
      className={`relative aspect-square w-full touch-pan-y overflow-hidden bg-surface-muted select-none ${
        dragging ? 'cursor-grabbing' : 'cursor-grab'
      }`}
    >
      {frames.map((src, frameIndex) => (
        <img
          key={src}
          src={src}
          alt={frameIndex === 0 ? productName : ''}
          aria-hidden={frameIndex !== 0}
          draggable={false}
          decoding="async"
          // All frames stay mounted and visibility is toggled: swapping a
          // single src causes a decode flash on every step.
          className={`absolute inset-0 h-full w-full object-cover ${
            frameIndex === index ? 'opacity-100' : 'opacity-0'
          }`}
        />
      ))}

      {!ready && (
        <div className="absolute inset-0 flex items-center justify-center bg-surface-muted" role="status">
          <span className="text-[0.625rem] uppercase tracking-[0.28em] text-muted">
            Preparing 360° view…
          </span>
        </div>
      )}

      {ready && (
        <span className="pointer-events-none absolute inset-x-0 bottom-4 flex items-center justify-center gap-2 text-[0.5625rem] uppercase tracking-[0.28em] text-ink/50">
          <RotateCw className="h-3 w-3" strokeWidth={1.5} aria-hidden="true" />
          Drag to rotate
        </span>
      )}

      {/* Announced position, for anyone driving this by keyboard. */}
      <span className="sr-only" aria-live="polite">
        Frame {index + 1} of {total}
      </span>
    </div>
  );
}
